import { Grid } from '@mui/material';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import { sessionService } from '../../application/SessionService';
import * as routing from '../../constants/Routing';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useAsUser } from '../../hooks/session/useAsUser';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import { intercom } from '../../services/intercom';
import { Backoffice } from '../backoffice';
import { Settings } from './../settings';
import NoProjects from './NoProjects';
import { ProjectComponent } from './ProjectComponent';
import { ShallNotPassComponent } from './ShallNotPassComponent';
import SideBar from './sidebar/SideBar';

const PrivateComponentContainer = styled(Grid)({
  height: `calc(100% - 8px)`,
  width: `100%`,
  backgroundColor: RoutalPalette.neutral05,
  overflow: `hidden`,
  margin: `4px 4px 4px 0px`,
  borderRadius: `10px`,
});

export const PrivateComponent = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: organization } = useRetrieveOrganization();
  const { data: user } = useRetrieveUser();
  const { asUser } = useAsUser();
  const isLogged = sessionService.isLogged();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();

  if (!isLogged) {
    if (sessionService.getAsUser()) {
      sessionService.deleteAsUser();
    }
    return <Navigate to={`/login${(location.pathname ?? ``) !== `` ? `?redirectTo=${location.pathname}` : ``}`} />;
  } else {
    const productTourId = searchParams.get(`product_tour_id`);
    const intercomChecklistId = searchParams.get(`intercom_checklist_id`);

    if (productTourId || intercomChecklistId) {
      if (productTourId) intercom.startTour(productTourId);
      if (intercomChecklistId) intercom.startChecklist(intercomChecklistId);
      return <Navigate to={location.pathname} />;
    }
  }

  const getPrivateRouting = () => {
    const privateRouting = [];

    if (!organization || !user) {
      return [];
    }

    if (!organization.active) {
      return [<Route key="shall-not-pass" path="/*" element={<ShallNotPassComponent />} />];
    }

    if (userCredentials?.isStaff(asUser) || userCredentials?.test(`can_manage_translations`)) {
      privateRouting.push(<Route key="backoffice" path="/backoffice/*" element={<Backoffice />} />);
    }

    privateRouting.push(<Route key="settings" path="/settings/*" element={<Settings />} />);
    privateRouting.push(<Route key="no_projects" path="/no_projects" element={<NoProjects />} />);

    if (!organization.subscription.isCanceled()) {
      privateRouting.push(<Route key="project" path="/:projectId/*" element={<ProjectComponent user={user} />} />);
      privateRouting.push(
        <Route
          key="redirect-1"
          path="*"
          element={
            <Navigate
              to={`${routing.PLANNER.ROOT.replace(`:projectId`, activeProject?.id ?? user?.default_project_id ?? ``)}`}
            />
          }
        />
      );
    } else {
      if (userCredentials?.test(`can_manage_billing`)) {
        privateRouting.push(
          <Route key="redirect-2" path="*" element={<Navigate to={routing.SETTINGS.BILLING.SUBSCRIPTION} />} />
        );
      } else {
        privateRouting.push(<Route key="redirect-3" path="/*" element={<ShallNotPassComponent />} />);
      }
    }

    return privateRouting;
  };

  return (
    <div
      style={{
        height: `100vh`,
        overflowY: `hidden`,
        display: `flex`,
        flexDirection: `row`,
        width: `100%`,
        backgroundColor: RoutalPalette.neutral05,
      }}
    >
      <SideBar />

      <PrivateComponentContainer container direction="row">
        <Routes>{user && organization ? <>{getPrivateRouting()}</> : null}</Routes>
      </PrivateComponentContainer>
    </div>
  );
};
